// dashboard/modals/MuteDelinkModal.js
// 2026-08-31 (phase 2 of the finalization redesign) — a clinic's
// self-service way to stop dealing with a patient without ever touching
// the pet itself: mute suppresses this clinic's alerts/dashboard
// visibility while the relationship stays intact (Case 1: transferred-out
// owner hasn't unlinked the old clinic yet); de-link actually severs the
// relationship, requiring the owner to re-link from scratch. Both are
// scoped entirely to pet_clinic_relationships — see migration 183.

import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { c } from '../constants';
import { useModalA11y } from '../useModalA11y';

export default function MuteDelinkModal({ petName, mode, onConfirm, onCancel, loading, error }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('transferred');
  const containerRef = useModalA11y(onCancel);

  const reasons = [
    { value: 'transferred', label: t('outcomeType.transferred') },
    { value: 'inactive',    label: t('outcomeType.inactive') },
    { value: 'unknown',     label: t('outcomeType.unknown') },
  ];

  const isDelink = mode === 'delink';

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(79,100,115,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div
        ref={containerRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mute-delink-modal-title"
        style={{ backgroundColor: c.white, borderRadius: 16, padding: 28, width: '100%', maxWidth: 440, boxShadow: '0 8px 32px rgba(79,100,115,0.18)', outline: 'none' }}
      >
        <h2 id="mute-delink-modal-title" style={{ fontSize: 17, fontWeight: 600, color: c.slate, marginBottom: 4 }}>
          {isDelink ? t('muteDelinkModal.delinkTitle', { petName }) : t('muteDelinkModal.muteTitle', { petName })}
        </h2>
        <p style={{ fontSize: 12, color: c.textSoft, marginBottom: 20 }}>
          {isDelink ? t('muteDelinkModal.delinkBody', { petName }) : t('muteDelinkModal.muteBody', { petName })}
        </p>

        <label htmlFor="mute-delink-reason" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: c.slate, marginBottom: 6 }}>{t('muteDelinkModal.reasonLabel')}</label>
        <select
          id="mute-delink-reason"
          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.border}`, backgroundColor: c.fog, fontSize: 13, marginBottom: 18 }}
          value={reason}
          onChange={e => setReason(e.target.value)}
        >
          {reasons.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        {error && <p role="alert" style={{ fontSize: 12, color: c.error, marginBottom: 12 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '8px 16px', background: 'none', border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 13, cursor: 'pointer', color: c.textSoft }}>{t('common.cancel')}</button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={loading}
            style={{ padding: '8px 16px', backgroundColor: isDelink ? c.error : c.slate, color: c.white, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1 }}
          >
            {loading
              ? t('muteDelinkModal.saving')
              : (isDelink ? t('muteDelinkModal.delinkButton') : t('muteDelinkModal.muteButton'))}
          </button>
        </div>
      </div>
    </div>
  );
}
